import React from "react";
import { useContext, useMemo } from "react";
import { useState } from "react";
import { LangContext } from "../../../context/lang/LangContext";
import { languages } from "../../../utils/lang";
import environments from "../../../utils/config/environments";
import { ProductSubmenu } from "../components/ProductSubmenu";
import { ProductSubMenuMobile } from "../components/ProductSubMenuMobile";
import { useGoogleAnalyticsEvent } from "../../../hooks/useGoogleAnalitycs";
import { ABCoin } from "../../Sections/AboutUs/AboutUs";
import { useLocation } from "@gatsbyjs/reach-router";
import {
  EventsAction,
  EventsCategories,
} from "../../../utils/enums/googleAnalyticsEvents";

export interface MenuItem {
  name: string;
  link: string;
  target: string;
  handleOpenSubMenu?: () => void;
  openSubMenu?: boolean;
  submenuElement?: React.ReactNode;
  subMenuElementMobile?: React.ReactNode;
}

export const useHeader = () => {
  const { messages } = useContext(LangContext);

  const [openMenu, setOpenMenu] = useState(false);
  const [openProductMenu, setOpenProductMenu] = useState(false);

  const location = useLocation();

  const abCoin = useMemo(() => {
    if (typeof window === "undefined") return ABCoin.A;
    const coin = localStorage.getItem("ABCoin");
    return coin as ABCoin;
  }, []);

  const [onClickBookADemo] = useGoogleAnalyticsEvent([
    {
      category: EventsCategories.EarlyAccess,
      label: "user click book a demo button on " + location.pathname,
      value: 1,
      action: EventsAction.Click,
      actionName: "book a demo" + abCoin,
    },
  ]);

  const currentLang = useMemo(() => {
    return (
      languages.find((lang) => lang.shortName === messages.currentLanguage) ||
      languages[0]
    );
  }, []);

  const handleOpenSubMenu = () => {
    setOpenProductMenu((prev) => !prev);
  };

  const onMouseLeaveProduct = () => {
    setOpenProductMenu(false);
  };

  const handleOpenMenu = () => {
    setOpenMenu(!openMenu);
  };

  const menuItems: MenuItem[] = useMemo((): MenuItem[] => {
    const menuItems = [
      {
        name: messages.header.buttons.benefits,
        link: `/${currentLang.code}/#benefits`,
        target: "_self",
      },
      {
        name: messages.header.buttons.product,
        link: `/${currentLang.code}/product`,
        target: "_self",
        handleOpenSubMenu: handleOpenSubMenu,
        openSubMenu: openProductMenu,
        submenuElement: (
          <ProductSubmenu
            openSubMenu={openProductMenu}
            handleCloseSubMenu={onMouseLeaveProduct}
          />
        ),
        subMenuElementMobile: (
          <ProductSubMenuMobile handleOpenSubMenu={handleOpenMenu} />
        ),
      },
      {
        name: messages.header.buttons.services,
        link: `/${currentLang.code}/#services`,
        target: "_self",
      },
      {
        name: messages.header.buttons.customers,
        link: `/${currentLang.code}/customers`,
        target: "_self",
      },
      {
        name: messages.header.buttons.forDevelopers,
        link: `/${currentLang.code}/for-developers`,
        target: "_self",
      },
    ];

    if (environments.showBlog === "true") {
      return menuItems;
    }

    return menuItems.filter(
      (item) => item.name !== messages.header.buttons.forDevelopers,
    );
  }, [messages, currentLang, openProductMenu]);

  return {
    menuItems,
    openMenu,
    handleOpenMenu,
    onClickBookADemo,
  };
};
